import { useMemo, useState } from 'react'
import { attachmentContentUrl } from '../../api/endpoints'
import type { Attachment } from '../../api/types'
import { Icon } from '../../components/Icon'
import { isImageType } from '../../lib/files'
import { fileTypeLabel, formatFileSize } from '../../lib/format'
import { useConversation } from '../conversation/ConversationContext'
import { saveAttachments } from '../conversation/attachmentFiles'

/**
 * The conversation's photos and files that this phone has (the messages it has loaded; older ones come as you scroll back
 * in the conversation): photos open in the viewer, documents save, as Save does in a message's menu.
 */
export function MediaSection() {
  const { messagesById, openAttachment } = useConversation()
  const [open, setOpen] = useState(false)
  const { photos, files } = useMemo(() => {
    const all = [...messagesById.values()]
      .filter((m) => !m.deletedAt)
      .sort((a, b) => b.seq - a.seq)
      .flatMap((m) => m.attachments)
      .filter((a) => a.status === 'ready')
    return { photos: all.filter((a) => isImageType(a.contentType)), files: all.filter((a) => !isImageType(a.contentType)) }
  }, [messagesById])
  const count = photos.length + files.length

  return (
    <>
      <button
        type="button"
        className="drawer-row"
        aria-expanded={open}
        aria-label={`Media and documents, ${count}`}
        onClick={() => setOpen((o) => !o)}
        disabled={count === 0}
      >
        <Icon name="image" />
        <span>Media and documents</span>
        <span className="drawer-row-value">{count}</span>
        <Icon name="chevronRight" />
      </button>
      {open ? (
        <div className="media-section">
          {photos.length ? (
            <div className="media-grid">
              {photos.map((photo) => (
                <PhotoTile key={photo.id} photo={photo} onOpen={() => openAttachment(photo)} />
              ))}
            </div>
          ) : null}
          {files.map((file) => (
            <button key={file.id} type="button" className="media-file" onClick={() => void saveAttachments([file])}>
              <span className="file-symbol" data-file-type={fileTypeLabel(file.fileName)}>
                {fileTypeLabel(file.fileName)}
              </span>
              <span className="file-info">
                <strong>{file.fileName}</strong>
                <small>{formatFileSize(file.sizeBytes)}</small>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </>
  )
}

function PhotoTile({ photo, onOpen }: { photo: Attachment; onOpen: () => void }) {
  return (
    <button type="button" className="media-tile" aria-label={`Open image: ${photo.fileName}`} onClick={onOpen}>
      <img src={attachmentContentUrl(photo.id, photo.hasThumb ? 'thumb' : 'original')} alt="" loading="lazy" decoding="async" />
    </button>
  )
}
